import * as XLSX from 'xlsx-js-style';

// Brand palette — kept in sync with FinancialResultPage's export so files
// from different pages feel like one product.
const PRIMARY = 'FF1D546D';
const ZEBRA   = 'FFF8FAFC'; // every other row
const SLATE   = 'FF334155';

/**
 * Detects whether a column is numeric (right-aligned, number-formatted).
 * Looks at up to the first 20 non-empty values for that key.
 */
function isNumericColumn(rows, key) {
  let seen = 0;
  for (const r of rows) {
    const v = r[key];
    if (v == null || v === '') continue;
    if (typeof v !== 'number' && Number.isNaN(Number(v))) return false;
    if (++seen >= 20) break;
  }
  return seen > 0;
}

/**
 * Export data to a styled Excel (.xlsx) file.
 *
 * Styling choices that you get for free:
 *   - Header row: bold white on dark-teal background, frozen so it stays
 *     visible while scrolling
 *   - Body rows: alternating off-white zebra stripes, slate-grey text
 *   - Numeric columns auto-detected → right-aligned + '#,##0.##' format
 *   - Column widths come from columns[*].width or default 18 chars
 *
 * @param {Array<Object>} rows - flat objects (each key = column)
 * @param {Array<{key, label, width?}>} columns - column definitions
 * @param {string} fileName - filename without extension
 * @param {string} [sheetName='Дані']
 */
export function exportToExcel(rows, columns, fileName, sheetName = 'Дані') {
  const numericKey = {};
  for (const c of columns) numericKey[c.key] = isNumericColumn(rows, c.key);

  const headerStyle = {
    font: { bold: true, sz: 12, color: { rgb: 'FFFFFFFF' } },
    fill: { fgColor: { rgb: PRIMARY } },
    alignment: { horizontal: 'left', vertical: 'center', wrapText: false },
    border: {
      bottom: { style: 'thin', color: { rgb: PRIMARY } },
    },
  };
  const dataStyle = (rIdx, isNumeric) => ({
    font: { sz: 11, color: { rgb: SLATE } },
    fill: { fgColor: { rgb: rIdx % 2 === 0 ? 'FFFFFFFF' : ZEBRA } },
    alignment: { horizontal: isNumeric ? 'right' : 'left', vertical: 'center' },
  });

  // Build sheet from cell objects so we can apply styles + types per cell.
  const ws = {};

  // Header row
  columns.forEach((c, cIdx) => {
    const addr = XLSX.utils.encode_cell({ r: 0, c: cIdx });
    ws[addr] = { v: c.label, t: 's', s: headerStyle };
  });

  // Body rows
  rows.forEach((row, rIdx) => {
    columns.forEach((c, cIdx) => {
      const addr = XLSX.utils.encode_cell({ r: rIdx + 1, c: cIdx });
      const raw = row[c.key];
      const isNumeric = numericKey[c.key];
      const baseStyle = dataStyle(rIdx, isNumeric);

      if (isNumeric && raw != null && raw !== '') {
        const n = Number(raw);
        ws[addr] = {
          v: Number.isFinite(n) ? n : raw,
          t: 'n',
          s: { ...baseStyle, numFmt: '#,##0.##' },
          z: '#,##0.##',
        };
      } else {
        ws[addr] = {
          v: raw ?? '',
          t: 's',
          s: baseStyle,
        };
      }
    });
  });

  ws['!ref'] = XLSX.utils.encode_range({
    s: { r: 0, c: 0 },
    e: { r: rows.length, c: columns.length - 1 },
  });
  ws['!cols'] = columns.map(c => ({ wch: c.width || 18 }));
  ws['!rows'] = [{ hpt: 22 }, ...rows.map(() => ({ hpt: 18 }))];
  // Freeze the header row so it stays visible while scrolling.
  ws['!freeze'] = { xSplit: 0, ySplit: 1 };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, `${fileName}.xlsx`);
}
